import { Dispatch, SetStateAction, useCallback, useRef, useState } from "react";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import handleException from "go-core/api/handleException";
import { apiOrganization } from "../../../../../../../../../../../services/Api/Organization/apiOrganization";
import { DeliveryEstimationApi } from "../../../../../../../../../../../services/Api/Organization/types";

type EstimateDeliveryParams = {
	orderId: number;
	setEstimatedDeliveryData: (data: DeliveryEstimationApi | undefined) => void;
	setEstimatingLoading: Dispatch<SetStateAction<boolean>>;
};

export const useEstimateDelivery = ({
	orderId,
	setEstimatedDeliveryData,
	setEstimatingLoading,
}: EstimateDeliveryParams) => {
	const { addFlash } = useFlash();
	const [isOutOfDeliveryZone, setIsOutOfDeliveryZone] = useState(false);
	const abortControllerRef = useRef<AbortController | null>(null);

	const handleEstimateDelivery = useCallback(
		async (selectedTimeOption: string | number | undefined, data?: string) => {
			setEstimatingLoading(true);

			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
			abortControllerRef.current = new AbortController();

			try {
				let scheduledDeliveryAt = typeof selectedTimeOption === "string" ? selectedTimeOption : undefined;

				let minutes = scheduledDeliveryAt ? 0 : typeof selectedTimeOption === "number" ? selectedTimeOption : 0;

				if (data) {
					scheduledDeliveryAt = data;
					minutes = 0;
				}

				const res = await apiOrganization.estimateOrderDelivery(
					orderId,
					"WOLT_DRIVE",
					minutes,
					scheduledDeliveryAt,
					{ abortController: abortControllerRef.current }
				);

				setEstimatedDeliveryData(res);
				setEstimatingLoading(false);
				return res;
			} catch (err) {
				const errors = handleException(err);

				if (errors?.[0]?.code === "ERR_CANCELED") return;

				if (
					errors?.[0]?.code === "dropoff_outside_of_delivery_area" ||
					errors?.[0]?.code === "order_has_no_coordinates"
				) {
					setIsOutOfDeliveryZone(true);
				} else {
					setEstimatedDeliveryData(undefined);
					handleError.alert(err, addFlash);
				}

				setEstimatingLoading(false);
			}
		},
		[orderId, apiOrganization, handleException, handleError, addFlash]
	);

	return {
		handleEstimateDelivery,
		isOutOfDeliveryZone,
	};
};
